import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  Alert,
} from '@mui/material';
import {
  Timer as TimerIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Notifications as AlarmIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

export interface TimerData {
  id: string;
  name: string;
  duration: number; // in seconds
  remaining: number; // in seconds
  isActive: boolean;
  isFinished: boolean;
}

interface KitchenTimerProps {
  stepName?: string;
  onTimerComplete?: (timerId: string, timerName: string) => void;
  autoStartTimer?: { duration: number; label: string } | null;
  onTimersUpdate?: (timers: TimerData[]) => void;
}

const KitchenTimer: React.FC<KitchenTimerProps> = ({ 
  stepName = 'Cooking Step',
  onTimerComplete,
  autoStartTimer,
  onTimersUpdate
}) => {
  const theme = useTheme();
  const [timers, setTimers] = useState<TimerData[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTimerName, setNewTimerName] = useState('');
  const [newTimerMinutes, setNewTimerMinutes] = useState('');
  const [newTimerSeconds, setNewTimerSeconds] = useState('');
  const intervalRefs = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio notification
  useEffect(() => {
    // Create a simple beep sound using Web Audio API
    const createBeepSound = () => {
      const AudioContextClass = window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const audioContext = new AudioContextClass();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 1);
    };

    audioRef.current = { play: createBeepSound } as HTMLAudioElement;
  }, []);

  // Timer interval management
  useEffect(() => {
    timers.forEach(timer => {
      if (timer.isActive && !timer.isFinished && timer.remaining > 0) {
        if (!intervalRefs.current[timer.id]) {
          intervalRefs.current[timer.id] = setInterval(() => {
            setTimers(prevTimers => 
              prevTimers.map(t => {
                if (t.id === timer.id && t.remaining > 0) {
                  const newRemaining = t.remaining - 1;
                  if (newRemaining === 0) {
                    // Timer finished
                    playAlarmSound();
                    onTimerComplete?.(t.id, t.name);
                    return { ...t, remaining: 0, isActive: false, isFinished: true };
                  }
                  return { ...t, remaining: newRemaining };
                }
                return t;
              })
            );
          }, 1000);
        }
      } else {
        if (intervalRefs.current[timer.id]) {
          clearInterval(intervalRefs.current[timer.id]);
          delete intervalRefs.current[timer.id];
        }
      }
    });

    return () => {
      Object.values(intervalRefs.current).forEach(interval => clearInterval(interval));
      intervalRefs.current = {};
    };
  }, [timers, onTimerComplete]);

  // Handle auto-starting timer from step suggestions
  useEffect(() => {
    if (autoStartTimer && !timers.some(timer => timer.name === autoStartTimer.label)) {
      const newTimer: TimerData = {
        id: Date.now().toString(),
        name: autoStartTimer.label,
        duration: autoStartTimer.duration * 60, // Convert minutes to seconds
        remaining: autoStartTimer.duration * 60,
        isActive: true, // Auto-start the timer
        isFinished: false,
      };
      setTimers(prev => [...prev, newTimer]);
    }
  }, [autoStartTimer, timers]);

  // Update parent component with timer changes
  useEffect(() => {
    if (onTimersUpdate) {
      onTimersUpdate(timers);
    }
  }, [timers, onTimersUpdate]);

  const playAlarmSound = () => {
    try {
      audioRef.current?.play();
      // Also show browser notification if permission granted
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Cook with Otto - Timer Complete!', {
          body: `Your ${stepName} timer has finished`,
          icon: '/favicon.ico',
        });
      }
    } catch (error) {
      // Fallback to visual notification only
    }
  };

  const addTimer = () => {
    const minutes = parseInt(newTimerMinutes) || 0;
    const seconds = parseInt(newTimerSeconds) || 0;
    const totalSeconds = minutes * 60 + seconds;

    if (totalSeconds > 0 && newTimerName.trim()) {
      const newTimer: TimerData = {
        id: Date.now().toString(),
        name: newTimerName.trim(),
        duration: totalSeconds,
        remaining: totalSeconds,
        isActive: false,
        isFinished: false,
      };

      setTimers(prev => [...prev, newTimer]);
      setNewTimerName('');
      setNewTimerMinutes('');
      setNewTimerSeconds('');
      setDialogOpen(false);
    }
  };

  const toggleTimer = (timerId: string) => {
    setTimers(prev =>
      prev.map(timer =>
        timer.id === timerId
          ? { ...timer, isActive: !timer.isActive }
          : timer
      )
    );
  };

  const resetTimer = (timerId: string) => {
    setTimers(prev =>
      prev.map(timer =>
        timer.id === timerId
          ? { 
              ...timer, 
              remaining: timer.duration, 
              isActive: false, 
              isFinished: false 
            }
          : timer
      )
    );
  };

  const deleteTimer = (timerId: string) => {
    if (intervalRefs.current[timerId]) {
      clearInterval(intervalRefs.current[timerId]);
      delete intervalRefs.current[timerId];
    }
    setTimers(prev => prev.filter(timer => timer.id !== timerId));
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerProgress = (timer: TimerData): number => {
    return ((timer.duration - timer.remaining) / timer.duration) * 100;
  };

  const openNewTimerDialog = () => {
    setNewTimerName(`${stepName} Timer`);
    setDialogOpen(true);
  };

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  return (
    <Box>
      {/* Timer List */}
      <AnimatePresence>
        {timers.map(timer => (
          <motion.div
            key={timer.id}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card 
              sx={{ 
                mb: 2,
                border: timer.isFinished 
                  ? `2px solid ${theme.palette.success.main}`
                  : timer.isActive 
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                backgroundColor: timer.isFinished 
                  ? 'rgba(76, 175, 80, 0.1)'
                  : 'background.paper',
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>
                    {timer.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleTimer(timer.id)}
                      disabled={timer.isFinished}
                      color={timer.isActive ? 'primary' : 'default'}
                    >
                      {timer.isActive ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => resetTimer(timer.id)}
                      color="secondary"
                    >
                      <StopIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => deleteTimer(timer.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      fontWeight: 700,
                      color: timer.isFinished 
                        ? 'success.main'
                        : timer.remaining <= 30 && timer.isActive
                          ? 'error.main'
                          : 'text.primary',
                    }}
                  >
                    {formatTime(timer.remaining)}
                  </Typography>
                  
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={getTimerProgress(timer)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: timer.isFinished 
                            ? theme.palette.success.main
                            : timer.remaining <= 30 && timer.isActive
                              ? theme.palette.error.main
                              : theme.palette.primary.main,
                        },
                      }}
                    />
                  </Box>
                  
                  {timer.isFinished && (
                    <Chip
                      icon={<AlarmIcon />}
                      label="Done!"
                      color="success"
                      size="small"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Add Timer Button */}
      <Button
        variant="outlined"
        fullWidth
        startIcon={<AddIcon />}
        onClick={openNewTimerDialog}
        sx={{
          mt: timers.length > 0 ? 2 : 0,
          borderStyle: 'dashed',
          borderWidth: 2,
          py: 1.5,
          '&:hover': {
            borderStyle: 'dashed',
            borderWidth: 2,
          },
        }}
      >
        Add Timer
      </Button>

      {/* New Timer Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimerIcon color="primary" />
            Add Kitchen Timer
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            label="Timer Name"
            value={newTimerName}
            onChange={(e) => setNewTimerName(e.target.value)}
            placeholder="e.g., Simmer sauce, Bake cookies"
            sx={{ mb: 3 }}
          />
          
          <Typography variant="subtitle1" gutterBottom>
            Timer Duration
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Minutes"
              type="number"
              value={newTimerMinutes}
              onChange={(e) => setNewTimerMinutes(e.target.value)}
              inputProps={{ min: 0, max: 999 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Seconds"
              type="number"
              value={newTimerSeconds}
              onChange={(e) => setNewTimerSeconds(e.target.value)}
              inputProps={{ min: 0, max: 59 }}
              sx={{ flex: 1 }}
            />
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Timer notifications will alert you when cooking times are complete, even if you navigate away.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={addTimer} 
            variant="contained"
            disabled={!newTimerName.trim() || (!parseInt(newTimerMinutes) && !parseInt(newTimerSeconds))}
          >
            Add Timer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KitchenTimer;